import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Textarea,
  NativeSelectRoot,
  NativeSelectField,
} from '@chakra-ui/react';
import { BirthRecordFormData, Dog } from '@/types/dog';
import { birthRecordService } from '@/services/firebase/birthRecordService';
import { useDogStore } from '@/stores/dogStore';

interface BirthRecordFormProps {
  userId: string;
  initialData?: BirthRecordFormData & { id?: string };
  onSubmitSuccess?: (recordId: string) => void;
  onCancel?: () => void;
}

/**
 * 出産記録フォームコンポーネント
 */
export const BirthRecordForm: FC<BirthRecordFormProps> = ({
  userId,
  initialData,
  onSubmitSuccess,
  onCancel,
}) => {
  const { dogs, fetchDogs } = useDogStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expectedDate, setExpectedDate] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<BirthRecordFormData & { matingDateStr?: string; birthDateStr: string }>();

  const matingDateStr = watch('matingDateStr');

  // 個体一覧取得（親犬選択用）
  useEffect(() => {
    if (userId) {
      fetchDogs(userId);
    }
  }, [userId, fetchDogs]);

  // 初期データのセット
  useEffect(() => {
    if (initialData) {
      setValue('motherId', initialData.motherId);
      setValue('fatherId', initialData.fatherId);
      setValue(
        'matingDateStr',
        initialData.matingDate ? initialData.matingDate.toISOString().split('T')[0] : ''
      );
      setValue('birthDateStr', initialData.birthDate.toISOString().split('T')[0]);
      setValue('totalPuppies', initialData.totalPuppies);
      setValue('alivePuppies', initialData.alivePuppies);
      setValue('deliveryType', initialData.deliveryType);
      setValue('notes', initialData.notes || '');
    }
  }, [initialData, setValue]);

  // 交配日から出産予定日を計算
  useEffect(() => {
    if (matingDateStr) {
      const matingDate = new Date(matingDateStr);
      const expected = birthRecordService.calculateExpectedBirthDate(matingDate);
      setExpectedDate(expected.toLocaleDateString('ja-JP'));
    } else {
      setExpectedDate(null);
    }
  }, [matingDateStr]);

  /**
   * フォーム送信
   */
  const onSubmit = async (data: BirthRecordFormData & { matingDateStr?: string; birthDateStr: string }) => {
    setIsLoading(true);
    setError(null);

    try {
      const formData: BirthRecordFormData = {
        motherId: data.motherId,
        fatherId: data.fatherId,
        matingDate: data.matingDateStr ? new Date(data.matingDateStr) : undefined,
        birthDate: new Date(data.birthDateStr),
        totalPuppies: Number(data.totalPuppies),
        alivePuppies: Number(data.alivePuppies),
        deliveryType: data.deliveryType,
        notes: data.notes,
      };

      if (initialData?.id) {
        // 更新
        await birthRecordService.updateBirthRecord(initialData.id, formData, userId);
        if (onSubmitSuccess) {
          onSubmitSuccess(initialData.id);
        }
      } else {
        // 新規登録
        const newRecord = await birthRecordService.createBirthRecord(formData, userId);
        if (onSubmitSuccess) {
          onSubmitSuccess(newRecord.id);
        }
      }
    } catch (err) {
      console.error('出産記録保存エラー:', err);
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // メス犬とオス犬の取得
  const femaleDogs = dogs.filter((dog: Dog) => dog.gender === 'female' && dog.status === 'active');
  const maleDogs = dogs.filter((dog: Dog) => dog.gender === 'male' && dog.status === 'active');

  return (
    <Box>
      {error && (
        <Box p={4} bg="red.50" borderRadius="md" borderWidth={1} borderColor="red.200" mb={4}>
          <Text fontSize="sm" color="red.700">
            {error}
          </Text>
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={6} align="stretch">
          {/* 母犬選択 */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              母犬（メス） <Text as="span" color="red.500">*</Text>
            </Text>
            <NativeSelectRoot>
              <NativeSelectField {...register('motherId', { required: '母犬を選択してください' })}>
                <option value="">選択してください</option>
                {femaleDogs.map((dog: Dog) => (
                  <option key={dog.id} value={dog.id}>
                    {dog.name} (#{dog.bioNumber})
                  </option>
                ))}
              </NativeSelectField>
            </NativeSelectRoot>
            {errors.motherId && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {errors.motherId.message}
              </Text>
            )}
          </Box>

          {/* 父犬選択 */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              父犬（オス） <Text as="span" color="red.500">*</Text>
            </Text>
            <NativeSelectRoot>
              <NativeSelectField {...register('fatherId', { required: '父犬を選択してください' })}>
                <option value="">選択してください</option>
                {maleDogs.map((dog: Dog) => (
                  <option key={dog.id} value={dog.id}>
                    {dog.name} (#{dog.bioNumber})
                  </option>
                ))}
              </NativeSelectField>
            </NativeSelectRoot>
            {errors.fatherId && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {errors.fatherId.message}
              </Text>
            )}
          </Box>

          {/* 交配日（任意） */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              交配日（任意）
            </Text>
            <Input type="date" {...register('matingDateStr')} />
            {expectedDate && (
              <Text fontSize="xs" color="gray.600" mt={1}>
                出産予定日（目安）: {expectedDate}（交配日+63日）
              </Text>
            )}
          </Box>

          {/* 出産日 */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              出産日 <Text as="span" color="red.500">*</Text>
            </Text>
            <Input
              type="date"
              {...register('birthDateStr', { required: '出産日を選択してください' })}
            />
            {errors.birthDateStr && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {errors.birthDateStr.message}
              </Text>
            )}
          </Box>

          {/* 総出産頭数 */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              総出産頭数 <Text as="span" color="red.500">*</Text>
            </Text>
            <Input
              type="number"
              min={0}
              {...register('totalPuppies', {
                required: '総出産頭数を入力してください',
                min: { value: 0, message: '0以上の値を入力してください' },
              })}
            />
            {errors.totalPuppies && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {errors.totalPuppies.message}
              </Text>
            )}
          </Box>

          {/* 生存頭数 */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              生存頭数 <Text as="span" color="red.500">*</Text>
            </Text>
            <Input
              type="number"
              min={0}
              {...register('alivePuppies', {
                required: '生存頭数を入力してください',
                min: { value: 0, message: '0以上の値を入力してください' },
              })}
            />
            {errors.alivePuppies && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {errors.alivePuppies.message}
              </Text>
            )}
          </Box>

          {/* 分娩方法 */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              分娩方法 <Text as="span" color="red.500">*</Text>
            </Text>
            <NativeSelectRoot>
              <NativeSelectField {...register('deliveryType', { required: true })}>
                <option value="">選択してください</option>
                <option value="normal">自然分娩</option>
                <option value="cesarean">帝王切開</option>
                <option value="other">その他</option>
              </NativeSelectField>
            </NativeSelectRoot>
            {errors.deliveryType && (
              <Text fontSize="sm" color="red.500" mt={1}>
                分娩方法を選択してください
              </Text>
            )}
          </Box>

          {/* 備考 */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              備考
            </Text>
            <Textarea rows={4} placeholder="特記事項があれば記入してください" {...register('notes')} />
          </Box>

          {/* ボタン */}
          <HStack gap={3}>
            {onCancel && (
              <Button variant="outline" onClick={onCancel} flex={1}>
                キャンセル
              </Button>
            )}
            <Button type="submit" colorPalette="blue" flex={1} loading={isLoading}>
              {initialData?.id ? '更新' : '登録'}
            </Button>
          </HStack>
        </VStack>
      </form>
    </Box>
  );
};
